import React, { useRef, useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { Loader2 } from 'lucide-react';

import { IVoice, useAIVoicesStore } from '../../zustand-store/useAIVoicesStore';
import { usePreGenAudioPlayer } from '../../zustand-store/usePreGenAudioPlayerStore';
import { PageParams } from './ConfigureVoiceModal';
import { Ico } from '../ui/Icons';
import { C } from '../../styles/tokens';
import { useOrganisation } from '../../http-hooks/organisation/useOrganisation';
import { useIntegration } from '../../http-hooks/integration/useIntegration';
import { useToggleVoiceBookmark } from '../../http-hooks/ai-voices/useToggleVoiceBookmark';
import { voiceBookmarkKey } from '../../utils/voiceBookmark';
import { withMp3Extension } from '../../utils/voicePreview';
import CreateAudioModal from '../content/CreateAudioModal';
import { addMixPanelEvent } from '../../store/actions';
import { CREATE_AUDIO_USECASE_SELECTED } from '../../constants/mixpanel-events';

// ── Constants ─────────────────────────────────────────────────────────────────

const GRADIENTS = [
    'linear-gradient(135deg,#667eea,#764ba2)',
    'linear-gradient(135deg,#f093fb,#f5576c)',
    'linear-gradient(135deg,#4facfe,#00f2fe)',
    'linear-gradient(135deg,#43e97b,#38f9d7)',
    'linear-gradient(135deg,#fa709a,#fee140)',
    'linear-gradient(135deg,#a18cd1,#fbc2eb)',
    'linear-gradient(135deg,#ffecd2,#fcb69f)',
    'linear-gradient(135deg,#a1c4fd,#c2e9fb)',
];

const ENGINE_LABEL: Record<string, string> = {
    emotional: 'Emotional',
    neural: 'Premium',
    standard: 'Standard',
};

const ENGINE_STYLE: Record<string, { color: string; bg: string; border: string }> = {
    emotional: { color: '#7C3AED', bg: '#EDE9FE', border: '#DDD6FE' },
    neural:    { color: '#B45309', bg: '#FEF3C7', border: '#FDE68A' },
    standard:  { color: '#475569', bg: '#F1F5F9', border: '#E2E8F0' },
};


function getFlag(langCode: string): string {
    const country = langCode?.split('-')[1];
    if (!country || country.length !== 2) return '';
    const upper = country.toUpperCase();
    return String.fromCodePoint(0x1F1E6 + upper.charCodeAt(0) - 65)
         + String.fromCodePoint(0x1F1E6 + upper.charCodeAt(1) - 65);
}

// ── Voice Avatar ──────────────────────────────────────────────────────────────

function VoiceAvatar({ name, gradient }: { name: string; gradient: string }) {
    return (
        <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
                width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                background: gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, fontWeight: 700, color: 'white',
            }}>
                {name[0]}
            </div>
            <div style={{
                position: 'absolute', bottom: -2, right: -2,
                width: 14, height: 14, borderRadius: '50%',
                background: '#111', border: '2px solid white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <svg width="7" height="6" viewBox="0 0 7 6" fill="none">
                    <path d="M1 3l1.8 2L6 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>
        </div>
    );
}

// ── More (···) dropdown ───────────────────────────────────────────────────────

interface MoreMenuProps {
    isActiveVoice: boolean;
    onSetDefault: () => void;
    onCreateProfile: () => void;
    onCreateVoiceover: () => void;
}

function MoreMenu({ isActiveVoice, onSetDefault, onCreateProfile, onCreateVoiceover }: MoreMenuProps) {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => {
            const target = e.target as Node;
            if (buttonRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', close);

       
        const closeOnReposition = () => setOpen(false);
        document.addEventListener('scroll', closeOnReposition, true);
        window.addEventListener('resize', closeOnReposition);

        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('scroll', closeOnReposition, true);
            window.removeEventListener('resize', closeOnReposition);
        };
    }, [open]);

    const itemStyle: React.CSSProperties = {
        display: 'block', width: '100%', textAlign: 'left',
        padding: '7px 14px', background: 'none', border: 'none',
        fontSize: 13, color: C.n700, cursor: 'pointer', whiteSpace: 'nowrap',
    };

    return (
        <div style={{ position: 'relative' }}>
            <button
                ref={buttonRef}
                onClick={e => {
                    e.stopPropagation();
                    // Render the dropdown in a portal (see below) so a scroll/overflow
                    // ancestor near the last row of a short list can't clip it — position
                    // it from the button's own screen coordinates instead of a relative parent.
                    const rect = buttonRef.current?.getBoundingClientRect();
                    if (rect) {
                        setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                    }
                    setOpen(o => !o);
                }}
                style={{
                    width: 28, height: 28, borderRadius: '50%', border: 'none',
                    background: 'transparent', cursor: 'pointer',
                    fontSize: 16, color: C.n400, lineHeight: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title="More options"
            >···</button>

            {open && menuPos && createPortal(
                <div
                    ref={menuRef}
                    style={{
                        position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 1000,
                        background: C.w, border: `1px solid ${C.n200}`, borderRadius: 10,
                        boxShadow: '0 4px 16px rgba(0,0,0,.10)', padding: '4px 0', minWidth: 170,
                    }}
                >
                    <button
                        style={{ ...itemStyle, color: isActiveVoice ? '#ca8a04' : C.n700 }}
                        onClick={() => { onSetDefault(); setOpen(false); }}
                    >
                        {isActiveVoice ? '⭐ Active voice' : 'Set as default'}
                    </button>
                    <button style={itemStyle} onClick={() => { onCreateProfile(); setOpen(false); }}>
                        Create custom profile
                    </button>
                    <button style={itemStyle} onClick={() => { onCreateVoiceover(); setOpen(false); }}>
                        Create voiceover
                    </button>
                </div>,
                document.body
            )}
        </div>
    );
}

// ── Voice Row ─────────────────────────────────────────────────────────────────

export interface VoiceRowProps {
    voice: IVoice;
    index: number;
    isLast: boolean;
    isPicker: boolean;
    saved: Set<string>;
    isSaving: boolean;
    onSave: (voice: IVoice) => void;
    langName: string;
}

export function VoiceRow({ voice, index, isLast, isPicker, saved, isSaving, onSave, langName }: VoiceRowProps) {
    const history = useHistory();
    const dispatch = useDispatch();
    const { org_id, id } = useParams<PageParams>();
    const { data: integration } = useIntegration({ org_id, integration_id: id });

    const instanceKey = `voicesv2:${voice.code}-${voice.engine}`;
    const { activeKey, isPlaying, loadingSrc, togglePlayPause } = usePreGenAudioPlayer();
    const audioURL = `${process.env.REACT_APP_VOICE_PREVIEW_CDN}/${withMp3Extension(voice.audio)}`;
    const active = isPlaying && activeKey === instanceKey;
    const isLoadingAudio = activeKey === instanceKey && !!loadingSrc;

    const [showCreateAudioModal, setShowCreateAudioModal] = useState(false);

    const audioProfileForVoice = {
        lang_code: voice.langCode,
        platform: voice.platform,
        engine: voice.engine,
        voice: voice.code,
        volume: 0,
        speed: 100,
        ...(voice.audio && { audio: voice.audio }),
        ...(voice.metadata && { metadata: voice.metadata }),
    };

    const handleUseCasePick = (useCase: string) => {
        setShowCreateAudioModal(false);
        dispatch(addMixPanelEvent({
            orgId: org_id, integrationId: id, body: {
                eventName: CREATE_AUDIO_USECASE_SELECTED,
                eventData: { useCase },
            },
        }));
        history.push({
            pathname: `/organisation/${org_id}/integration/${id}/content/new`,
            state: { audioProfile: audioProfileForVoice },
        });
    };

    const { selectedVoice, setSelectedVoice, toggleCreateCustomVoiceProfileModal, toggleSetDefaultProfile } =
        useAIVoicesStore(state => ({
            selectedVoice: state.selectedVoice,
            setSelectedVoice: state.setSelectedVoice,
            toggleCreateCustomVoiceProfileModal: state.toggleCreateCustomVoiceProfileModal,
            toggleSetDefaultProfile: state.toggleSetDefaultProfile,
        }), shallow);

    const isActiveVoice =
        integration?.audio_profile?.voice === voice.code &&
        integration?.audio_profile?.engine === voice.engine &&
        integration?.audio_profile?.platform === voice.platform;

    const isSaved   = saved.has(voiceBookmarkKey(voice));
    const gradient  = GRADIENTS[index % GRADIENTS.length];
    const engStyle  = ENGINE_STYLE[voice.engine] ?? ENGINE_STYLE.standard;
    const flag      = getFlag(voice.langCode);
    const genderLbl = voice.gender ? voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1) : '';
    const stylesCount = voice.styles?.length ?? 0;

    return (
        <div
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px',
                borderBottom: isLast ? 'none' : `1px solid ${C.n100}`,
                transition: 'background .1s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = C.n50; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
        >
            {/* Avatar + Name + preview text (desktop) / engine tag (mobile only) */}
            <div className="voice-row-name" style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <VoiceAvatar name={voice.name} gradient={gradient} />
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.n900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {voice.name}{' '}
                        <span style={{ fontSize: 11, color: C.n400, fontWeight: 500 }}>({genderLbl})</span>
                    </div>
                    {/* Engine tag — mobile only (desktop shows it as a separate column) */}
                    <span className="voice-engine-inline" style={{
                        marginTop: 3,
                        padding: '2px 7px', fontSize: 10.5, fontWeight: 600, borderRadius: 5,
                        color: engStyle.color, background: engStyle.bg, border: `1px solid ${engStyle.border}`,
                    }}>
                        {ENGINE_LABEL[voice.engine] ?? voice.engine}
                    </span>
                </div>
            </div>

            {/* Language — hidden on mobile via CSS class */}
            <div className="voice-row-lang" style={{ alignItems: 'center', gap: 5, minWidth: 0 }}>
                {flag && <span style={{ fontSize: 13 }}>{flag}</span>}
                <span style={{ fontSize: 12, color: C.n600, whiteSpace: 'nowrap' }}>{langName}</span>
                {stylesCount > 0 && (
                    <span style={{ fontSize: 10.5, color: C.p600, background: C.p50, padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>
                        +{stylesCount}
                    </span>
                )}
            </div>


            <div className="voice-row-spacer" />

            {/* Engine badge — desktop only, separate column */}
            <span className="voice-row-engine" style={{
                padding: '3px 8px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                color: engStyle.color, background: engStyle.bg, border: `1px solid ${engStyle.border}`,
                marginRight: 4, flexShrink: 0, whiteSpace: 'nowrap',
            }}>
                {ENGINE_LABEL[voice.engine] ?? voice.engine}
            </span>

            {/* Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                {/* Play */}
                <button
                    onClick={() => {
                        if (isLoadingAudio) return;
                        if (!active) {
                            dispatch(addMixPanelEvent({
                                orgId: org_id, integrationId: id, body: {
                                    eventName: "VOICE_PREVIEWED",
                                    eventData: { voice: voice.code, engine: voice.engine, langCode: voice.langCode },
                                }
                            }));
                        }
                        // Stream via HTML5 Audio so short previews start at canplay
                        // instead of waiting for the full Web Audio download + decode.
                        togglePlayPause(audioURL, instanceKey, { html5: true });
                    }}
                    disabled={isLoadingAudio}
                    title={isLoadingAudio ? 'Loading…' : active ? 'Pause' : 'Preview'}
                    style={{
                        width: 28, height: 28, borderRadius: '50%', border: 'none',
                        background: active ? C.p600 : C.n100,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: isLoadingAudio ? 'not-allowed' : 'pointer', transition: 'background .15s',
                    }}
                >
                    {isLoadingAudio
                        ? <Loader2 size={10} color={active ? C.w : C.n600} className="vi-spin" />
                        : <Ico n={active ? 'pause' : 'play'} size={10} color={active ? C.w : C.n600} />
                    }
                </button>

                {/* Star / save */}
                <button
                    onClick={e => { e.stopPropagation(); onSave(voice); }}
                    disabled={isSaving}
                    title={isSaved ? 'Unsave' : 'Save'}
                    style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: isSaved ? '#FEF3C7' : 'transparent',
                        border: `1px solid ${isSaved ? '#FCD34D' : C.n200}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: isSaving ? 'default' : 'pointer', transition: 'all .15s',
                        opacity: isSaving ? 0.5 : 1,
                    }}
                >
                    <svg width="12" height="12" viewBox="0 0 24 24"
                        fill={isSaved ? '#F59E0B' : 'none'}
                        stroke={isSaved ? '#F59E0B' : C.n400} strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                </button>

                {/* More — only in non-picker (explore page) mode */}
                {!isPicker && (
                    <MoreMenu
                        isActiveVoice={isActiveVoice}
                        onSetDefault={() => { setSelectedVoice(voice); toggleSetDefaultProfile(); }}
                        onCreateProfile={() => { setSelectedVoice(voice); toggleCreateCustomVoiceProfileModal(); }}
                        onCreateVoiceover={() => setShowCreateAudioModal(true)}
                    />
                )}
            </div>

            <CreateAudioModal
                show={showCreateAudioModal}
                onClose={() => setShowCreateAudioModal(false)}
                onPick={handleUseCasePick}
            />
        </div>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────

interface IVoicesProps {
    isPicker: boolean;
    searchQuery?: string;
    // UI-only: lets a parent (e.g. AIVoicesV2) mirror the filtered count into an
    // "active filter summary" banner without duplicating the filtering logic here.
    onCountChange?: (count: number) => void;
}

export const VoicesV2 = ({ isPicker, searchQuery: externalSearchQuery, onCountChange }: IVoicesProps) => {
    // Read voices (already filtered by store filters) + selectedLanguage for display.
    // Use two separate subscriptions so we get primitive-based change detection for
    // the language code — object reference equality on selectedLanguage fails when
    // switching back to a previously-visited language (same object in the map).
    const voices          = useAIVoicesStore(state => state.voices);
    const selectedLangCode = useAIVoicesStore(state => state.selectedLanguage?.code ?? '');
    const selectedLangName = useAIVoicesStore(state => state.selectedLanguage?.name ?? '');

    const { org_id } = useParams<PageParams>();
    const { data: organisation } = useOrganisation({ org_id });
    const toggleVoiceBookmark = useToggleVoiceBookmark();
    const dispatch = useDispatch();

    // Bookmarks are org-level (shared across the whole org), not per-user —
    // sourced from the org record instead of local state so they persist.
    // Keyed by "code#engine" (matching the backend's map key) since a voice
    // code can repeat across engine tiers (emotional/neural/standard).
    const saved = useMemo(() => {
        const bookmarkedVoices: Record<string, unknown> = organisation?.bookmarked_voices ?? {};
        return new Set(Object.keys(bookmarkedVoices));
    }, [organisation?.bookmarked_voices]);

    const pendingBookmarkKey = toggleVoiceBookmark.isLoading && toggleVoiceBookmark.variables
        ? voiceBookmarkKey(toggleVoiceBookmark.variables.voice)
        : null;

    const searchQuery = externalSearchQuery ?? '';

    // voices from the store is already filtered by gender/quality/style.
    // We only need to apply the search query, then sort.
    const filteredVoices = useMemo(() => {
        if (searchQuery.trim()) return voices.filter(v => v.name.toLowerCase().includes(searchQuery.toLowerCase()));
        return voices;
    // selectedLangCode is a primitive string dep — ensures recompute on every
    // language switch even when the voices array reference is the same.
    }, [voices, selectedLangCode, searchQuery]);

    useEffect(() => {
        onCountChange?.(filteredVoices.length);
    }, [filteredVoices.length, onCountChange]);

    // Debounced "Voice Browsed" — fires once search settles, not per keystroke.
    useEffect(() => {
        if (!searchQuery.trim()) return;
        const timeout = setTimeout(() => {
            dispatch(addMixPanelEvent({
                orgId: org_id, body: {
                    eventName: "VOICE_BROWSED",
                    eventData: { search_query: searchQuery, lang_code: selectedLangCode, result_count: filteredVoices.length },
                }
            }));
        }, 800);
        return () => clearTimeout(timeout);
    }, [searchQuery, selectedLangCode, filteredVoices.length, dispatch, org_id]);

    const handleSave = (voice: IVoice) => {
        toggleVoiceBookmark.mutate({ org_id, voice });
    };

    return (
        <div style={{ marginTop: 8 }}>
            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 14 }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: C.n900, margin: 0 }}>
                    All voices{selectedLangName ? ` — ${selectedLangName}` : ''}
                </h2>
                <Ico n="chevR" size={14} color={C.n400} />
            </div>

            {filteredVoices.length === 0 ? (
                <div style={{ color: C.n400, fontSize: 14, padding: '24px 0' }}>
                    {searchQuery.trim()
                        ? `No voices found matching "${searchQuery}"`
                        : 'No voices matching the filters could be found.'}
                </div>
            ) : (
                <div style={{ background: C.w, border: `1px solid ${C.n200}`, borderRadius: 13, overflow: 'hidden' }}>
                    {filteredVoices.map((voice, i) => (
                        <VoiceRow
                            key={`${voice.code}-${voice.engine}`}
                            voice={voice}
                            index={i}
                            isLast={i === filteredVoices.length - 1}
                            isPicker={isPicker}
                            saved={saved}
                            isSaving={pendingBookmarkKey === voiceBookmarkKey(voice)}
                            onSave={handleSave}
                            langName={selectedLangName || voice.langCode}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
